import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addCompIndirectSoldToBusiness,
  getCompIndirectSoldList,
  updateCompIndirectSold,
  deleteCompIndirectSold,
} from "../../../../../../apis/Business/CompanyIndirectUseOfSoldApi";
import {
  getCompIndirectEmitDropdown,
  getCompIndirectEnergyDropdown,
  getTransportCountryData,
  getCompUseGwpDropdown,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const IndirectUseSoldTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownProcess, setDropdownProcess] = useState([]);
  const [dropdownCountry, setDropdownCountry] = useState([]);
  const [dropdownGWP, setDropdownGWP] = useState([]);
  const [dropdownEnergy, setDropdownEnergy] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getCompIndirectSoldList({
      businessId,
      setObjects: setData,
    });
    getCompIndirectEmitDropdown({ setDropdown: setDropdownProcess });
    getTransportCountryData({ setDropdownData: setDropdownCountry });
    getCompUseGwpDropdown({ setDropdown: setDropdownGWP });
    getCompIndirectEnergyDropdown({ setDropdown: setDropdownEnergy });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownProcess?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["process_emitted.value"]?.value || row.process_emitted?.value;

        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) =>
              option?.dropdown === materialValue ||
              option?.dropdown_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "process_emitted.value": {
        targetField: "amount_per_use",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedItem = dropdownData.find(
            (item) =>
              item.dropdown === selectedValue ||
              item.dropdown_en === selectedValue
          );
          if (selectedItem) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedItem.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedItem.unit,
              }));
            }
          }
        },
        dropdownDataKey: "process_emitted.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      updateCompIndirectSold({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        process_emitted: editedItem["process_emitted.value"]
          ? editedItem["process_emitted.value"].value
          : editedItem["process_emitted"]
          ? editedItem["process_emitted"].value
          : null,
        country_sold: editedItem["country_sold.value"]
          ? editedItem["country_sold.value"].value
          : editedItem["country_sold"]
          ? editedItem["country_sold"].value
          : null,
        greenhouse: editedItem["greenhouse.value"]
          ? editedItem["greenhouse.value"].value
          : editedItem["greenhouse"]
          ? editedItem["greenhouse"].value
          : null,
        energy_added: editedItem["energy_added.value"]
          ? editedItem["energy_added.value"].value
          : editedItem["energy_added"]
          ? editedItem["energy_added"].value
          : null,
        amount_per_use: editedItem["customMulti_amount_per_use"]
          ? editedItem["customMulti_amount_per_use"]
          : editedItem["amount_per_use"],
        amount_per_lifetime: editedItem["customMulti_amount_per_lifetime"]
          ? editedItem["customMulti_amount_per_lifetime"]
          : editedItem["amount_per_lifetime"],
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addCompIndirectSoldToBusiness({
        businessId,
        ...newItem,
        process_emitted: newItem["process_emitted.value"]
          ? newItem["process_emitted.value"].value
          : null,
        country_sold: newItem["country_sold.value"]
          ? newItem["country_sold.value"].value
          : null,
        greenhouse: newItem["greenhouse.value"]
          ? newItem["greenhouse.value"].value
          : null,
        energy_added: newItem["energy_added.value"]
          ? newItem["energy_added.value"].value
          : null,
        amount_per_use: newItem["customMulti_amount_per_use"],
        amount_per_lifetime: newItem["customMulti_amount_per_lifetime"],
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteCompIndirectSold({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "product_name",
        header: t("PRODUCT_NAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "country_sold.value",
        header: `${t("COMP_DIRECT_COUNTRY")}`,
        type: "dropdown",
        dropdownData: dropdownCountry,
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("COUNTRY_SOLD_TIP"),
      },
      {
        accessor: "percentage_sold",
        header: `${t("COMP_DIRECT_QTY")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("QTY_SOLD_COUNTRY_TIP"),
      },
      {
        accessor: "weight_product",
        header: `${t("COMP_INDIRECT_WEIGHT")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
      },

      {
        accessor: "process_emitted.value",
        header: `${t("COMP_PROCESS_EMIT")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "150px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_EMIT_COMP_TIP"),
      },
      {
        accessor: "energy_added.value",
        header: `${t("COMP_INDIRECT_ENERGY_ADDED")}`,
        type: "dropdown",
        dropdownData: dropdownEnergy,
        disableSortBy: true,
        width: "150px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_USE_PHASE_COMP_TIP"),
      },
      {
        accessor: "energy_added_times",
        header: `${t("COMP_INDIRECT_ENERGY_TIMES")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        isNABtn: true,
        tipHeader: "Tip",
        tipBody: t("INDIRECT_ENERGY_ADDED_COMP_TIP"),
      },

      {
        accessor: "customMulti",
        header: `${t("COMP_EXPECTED_AMOUNT")}`,
        type: "multipleCustom",
        wide: true,
        layout: "horizontal",
        optional: true,
        width: "300px",
        customInputs: [
          {
            accessor: "amount_per_use",
            header: `${t("per use")}`,
            type: "number",
            disableSortBy: true,
            optional: true,
            placeholder: "0",
            width: "60px",
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per use")}`,
            isNA: true,
            accessorNA: "energy_added_times",
          },
          {
            accessor: "amount_per_lifetime",
            header: `${t("per lifetime")}`,
            type: "number",
            optional: true,
            disableSortBy: true,
            width: "60px",
            unit: (rowId) => `${unitMaterials[rowId] || "Unit"}`,
            additionalText: `${t("per life")}`,
            isNA: false,
            accessorNA: "energy_added_times",
          },
        ],
      },
      {
        accessor: "greenhouse.value",
        header: `${t("COMP_GREENHOUSE")}`,
        type: "dropdown",
        dropdownData: dropdownGWP,
        disableSortBy: true,
        width: "130px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("GHG_TIP"),
      },
      {
        accessor: "gas_charged",
        header: `${t("COMP_GWP_NUM")}`,
        type: "number",
        optional: true,
        disableSortBy: true,
        tipHeader: "Tip",
        tipBody: t("GHG_QTY_TIP"),
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [
      dropdownProcess,
      dropdownCountry,
      unitMaterials,
      dropdownGWP,
      dropdownEnergy,
      t,
    ]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "process_emitted.value": dropdownProcess,
      "country_sold.value": dropdownCountry,
      "greenhouse.value": dropdownGWP,
      "energy_added.value": dropdownEnergy,
    }),
    [dropdownProcess, dropdownCountry, dropdownGWP, dropdownEnergy]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default IndirectUseSoldTable;
